// src/components/TransportBar.tsx
"use client";

import {
  type CSSProperties,
  type FocusEvent,
  type FormEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Pause,
  Play,
  SkipBack,
  SkipForward,
  SpeakerHigh,
  SpeakerSlash,
  StopCircle,
} from "@/components/ui/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";


type Props = {
  isPlaying: boolean;
  onPlayPause: () => void;
  onPrev: () => void;
  onNext: () => void;
  trackTitle?: string | null;
  trackArtist?: string | null;
  trackUri?: string | null;
  positionMs?: number;
  durationMs?: number;
  onSeek?: (ms: number) => void;
  volume?: number;
  onVolumeChange?: (next: number) => void;
  onEndSession?: () => void;
  endSessionDisabled?: boolean;
  playbackDisabled?: boolean;
};

type TransportIconButtonProps = {
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
  className?: string;
  tone?: "primary" | "secondary" | "tertiary";
  children: ReactNode;
};

const TRANSPORT_CLEARANCE_GAP_PX = 12;

const transportIconButtonBaseClass =
  "inline-flex size-[2.15rem] items-center justify-center rounded-full border transition-[transform,background,color,border-color,box-shadow] duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)] hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60 lg:size-9";

function TransportIconButton({
  ariaLabel,
  disabled,
  onClick,
  className,
  tone = "secondary",
  children,
}: TransportIconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        transportIconButtonBaseClass,
        tone === "primary"
          ? "theme-button-primary"
          : tone === "tertiary"
            ? "theme-button-tertiary"
            : "theme-button-secondary",
        className,
      )}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

export default function TransportBar({
  isPlaying,
  onPlayPause,
  onPrev,
  onNext,
  trackTitle,
  trackArtist,
  trackUri,
  positionMs,
  durationMs,
  onSeek,
  volume = 0.8,
  onVolumeChange,
  onEndSession,
  endSessionDisabled = false,
  playbackDisabled = false,
}: Props) {
  const safePosition = positionMs ?? 0;
  const safeDuration = durationMs ?? 0;
  const currentTrackKey = `${trackTitle ?? ""}::${trackArtist ?? ""}::${safeDuration}`;
  const [displayPositionMs, setDisplayPositionMs] = useState(safePosition);
  const sourcePositionMsRef = useRef(safePosition);
  const sourceDurationMsRef = useRef(safeDuration);
  const isPlayingRef = useRef(isPlaying);
  const wasPlayingRef = useRef(isPlaying);
  const trackKeyRef = useRef(currentTrackKey);
  const forceSyncMsRef = useRef<number | null>(null);
  const progressAnchorMsRef = useRef(safePosition);
  const progressAnchorTsRef = useRef<number>(0);
  const progressRafRef = useRef<number | null>(null);
  const isSeekDraggingRef = useRef(false);
  const lastSeekRequestRef = useRef<{
    positionMs: number;
    atMs: number;
  } | null>(null);
  const [seekPreview, setSeekPreview] = useState<{
    trackKey: string;
    percent: number;
  } | null>(null);
  const safeVolume = Math.min(1, Math.max(0, volume));
  const volumePercent = Math.round(safeVolume * 100);
  const controlsDisabled = Boolean(playbackDisabled);
  const lastAudibleVolumeRef = useRef(safeVolume > 0 ? safeVolume : 0.8);
  const transportBarRef = useRef<HTMLDivElement | null>(null);
  const safeTitle = trackTitle?.trim() || "No track playing";
  const safeArtist = trackArtist?.trim() || "";

  const progressPercent =
    safeDuration > 0
      ? Math.min(100, (displayPositionMs / safeDuration) * 100)
      : 0;
  const activeProgressPercent =
    seekPreview?.trackKey === currentTrackKey
      ? seekPreview.percent
      : progressPercent;
  const progressStyle = {
    "--transport-progress-pct": `${activeProgressPercent}%`,
  } as CSSProperties;
  const progressHighlightStyle = {
    width:
      safeDuration > 0 && activeProgressPercent > 0.05
        ? `max(0.65rem, ${activeProgressPercent}%)`
        : "0px",
    opacity: controlsDisabled ? 0.4 : 0.92,
  } as CSSProperties;
  const volumeStyle = {
    "--transport-volume-pct": `${volumePercent}%`,
  } as CSSProperties;

  useEffect(() => {
    if (trackKeyRef.current !== currentTrackKey) {
      trackKeyRef.current = currentTrackKey;
      forceSyncMsRef.current = safePosition;
      isSeekDraggingRef.current = false;
    }
    sourcePositionMsRef.current = safePosition;
    sourceDurationMsRef.current = safeDuration;
    isPlayingRef.current = isPlaying;
  }, [currentTrackKey, isPlaying, safeDuration, safePosition]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const clamp = (value: number, duration: number) => {
      if (duration > 0) {
        return Math.max(0, Math.min(value, duration));
      }
      return Math.max(0, value);
    };

    const tick = () => {
      const now = window.performance.now();
      const sourceDuration = sourceDurationMsRef.current;
      const sourcePosition = clamp(sourcePositionMsRef.current, sourceDuration);
      const forcedSync = forceSyncMsRef.current;

      if (forcedSync != null) {
        const forced = clamp(forcedSync, sourceDuration);
        forceSyncMsRef.current = null;
        progressAnchorMsRef.current = forced;
        progressAnchorTsRef.current = now;
        wasPlayingRef.current = isPlayingRef.current;
        setDisplayPositionMs((prev) => (Math.abs(prev - forced) >= 1 ? forced : prev));
        progressRafRef.current = window.requestAnimationFrame(tick);
        return;
      }

      if (isPlayingRef.current) {
        if (!wasPlayingRef.current) {
          progressAnchorMsRef.current = sourcePosition;
          progressAnchorTsRef.current = now;
          wasPlayingRef.current = true;
        }

        const elapsed = Math.max(0, now - progressAnchorTsRef.current);
        const predicted = progressAnchorMsRef.current + elapsed;
        const predictedClamped = clamp(predicted, sourceDuration);
        const drift = sourcePosition - predictedClamped;
        let nextDisplay = predictedClamped;
        let allowBackwards = false;

        if (drift > 250) {
          progressAnchorMsRef.current = sourcePosition;
          progressAnchorTsRef.current = now;
          nextDisplay = sourcePosition;
        } else if (drift < -1000) {
          const likelySeekBack =
            sourcePosition + 2000 < progressAnchorMsRef.current;
          if (likelySeekBack) {
            progressAnchorMsRef.current = sourcePosition;
            progressAnchorTsRef.current = now;
            nextDisplay = sourcePosition;
            allowBackwards = true;
          }
        }

        setDisplayPositionMs((prev) =>
          Math.abs(prev - nextDisplay) >= 1
            ? !allowBackwards && nextDisplay < prev
              ? prev
              : nextDisplay
            : prev
        );
      } else {
        wasPlayingRef.current = false;
        progressAnchorMsRef.current = sourcePosition;
        progressAnchorTsRef.current = now;
        setDisplayPositionMs((prev) =>
          Math.abs(prev - sourcePosition) >= 1 ? sourcePosition : prev
        );
      }

      progressRafRef.current = window.requestAnimationFrame(tick);
    };

    progressRafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (progressRafRef.current != null) {
        window.cancelAnimationFrame(progressRafRef.current);
        progressRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (safeVolume > 0.001) {
      lastAudibleVolumeRef.current = safeVolume;
    }
  }, [safeVolume]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const transportBar = transportBarRef.current;
    if (!transportBar) return;

    const rootStyle = document.documentElement.style;
    let frameId = 0;
    let resizeObserver: ResizeObserver | null = null;

    const syncTransportClearance = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const rect = transportBar.getBoundingClientRect();
        const clearancePx = Math.max(
          0,
          Math.ceil(window.innerHeight - rect.top + TRANSPORT_CLEARANCE_GAP_PX)
        );
        rootStyle.setProperty(
          "--session-transport-clearance-runtime",
          `${clearancePx}px`
        );
      });
    };

    syncTransportClearance();
    window.addEventListener("resize", syncTransportClearance);
    window.addEventListener("orientationchange", syncTransportClearance);

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(syncTransportClearance);
      resizeObserver.observe(transportBar);
    }

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", syncTransportClearance);
      window.removeEventListener("orientationchange", syncTransportClearance);
      rootStyle.removeProperty("--session-transport-clearance-runtime");
    };
  }, []);

  const seekToPercent = (pct: number) => {
    if (!onSeek || safeDuration <= 0 || controlsDisabled) return;
    const newPos = (pct / 100) * safeDuration;
    const nowMs = Date.now();
    const lastSeekRequest = lastSeekRequestRef.current;
    if (
      lastSeekRequest &&
      Math.abs(lastSeekRequest.positionMs - newPos) < 1 &&
      nowMs - lastSeekRequest.atMs < 250
    ) {
      return;
    }
    lastSeekRequestRef.current = {
      positionMs: newPos,
      atMs: nowMs,
    };
    const now =
      typeof window !== "undefined" && typeof window.performance !== "undefined"
        ? window.performance.now()
        : Date.now();
    sourcePositionMsRef.current = newPos;
    forceSyncMsRef.current = newPos;
    progressAnchorMsRef.current = newPos;
    progressAnchorTsRef.current = now;
    setDisplayPositionMs(newPos);
    onSeek(newPos);
  };

  const clampPercent = (value: number) =>
    Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));

  const readRangeValue = (event: { currentTarget: HTMLInputElement }) =>
    clampPercent(Number(event.currentTarget.value));

  const handleSeekInput = (e: FormEvent<HTMLInputElement>) => {
    const pct = readRangeValue(e);
    if (isSeekDraggingRef.current) {
      setSeekPreview({
        trackKey: currentTrackKey,
        percent: pct,
      });
      return;
    }

    seekToPercent(pct);
  };

  const commitSeek = (pct: number) => {
    isSeekDraggingRef.current = false;
    setSeekPreview(null);
    seekToPercent(pct);
  };

  const handleSeekPointerDown = (e: PointerEvent<HTMLInputElement>) => {
    if (controlsDisabled || safeDuration <= 0) return;
    isSeekDraggingRef.current = true;
    setSeekPreview({
      trackKey: currentTrackKey,
      percent: readRangeValue(e),
    });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handleSeekPointerUp = (e: PointerEvent<HTMLInputElement>) => {
    if (!isSeekDraggingRef.current) return;
    commitSeek(readRangeValue(e));
  };

  const handleSeekBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (!isSeekDraggingRef.current) return;
    commitSeek(readRangeValue(e));
  };

  const handleVolumeInput = (e: FormEvent<HTMLInputElement>) => {
    if (!onVolumeChange) return;
    const pct = readRangeValue(e);
    onVolumeChange(pct / 100);
  };

  const handleToggleMute = () => {
    if (!onVolumeChange) return;

    if (safeVolume > 0.001) {
      lastAudibleVolumeRef.current = safeVolume;
      onVolumeChange(0);
      return;
    }

    onVolumeChange(lastAudibleVolumeRef.current > 0 ? lastAudibleVolumeRef.current : 0.75);
  };

  return (
    <div
      ref={transportBarRef}
      data-transport-bar
      className="fixed inset-x-0 bottom-0 z-[70] pl-[max(0.5rem,env(safe-area-inset-left))] pr-[max(0.5rem,env(safe-area-inset-right))]"
    >
      <div
        className="session-depth-transport theme-surface-panel relative mx-auto w-full max-w-[var(--layout-user-shell-max)] overflow-visible rounded-[var(--session-section-radius-xl,1.45rem)] border-0"
        style={{
          backgroundColor:
            "var(--session-depth-transport-surface-base, color-mix(in srgb, var(--surface-panel) 72%, var(--color-background) 28%))",
          background:
            "var(--session-depth-transport-surface, linear-gradient(180deg, color-mix(in srgb, var(--surface-panel) 74%, var(--color-background) 26%) 0%, color-mix(in srgb, var(--surface-panel) 84%, var(--color-background) 16%) 100%))",
          boxShadow:
            "var(--session-depth-transport-shadow, 0 24px 52px color-mix(in srgb, black 52%, transparent), inset 0 1px 0 rgba(255,255,255,0.1))",
          backdropFilter:
            "var(--session-depth-transport-backdrop, blur(18px) saturate(1.18))",
          WebkitBackdropFilter:
            "var(--session-depth-transport-backdrop, blur(18px) saturate(1.18))",
        }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          aria-hidden="true"
          style={{
            background:
              "var(--session-depth-transport-top-line, linear-gradient(90deg, transparent, color-mix(in srgb, var(--session-accent-border) 72%, transparent), transparent))",
          }}
        />
        <div className="relative z-20 h-0 overflow-visible">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-[2px] -translate-y-[62%]"
            data-transport-progress-effects=""
          >
            <div className="relative h-6 w-full">
              <div
                className="absolute left-0 top-[calc(50%+2px)] h-[0.72rem] -translate-y-1/2 rounded-full blur-[10px]"
                style={{
                  ...progressHighlightStyle,
                  background:
                    "linear-gradient(90deg, color-mix(in srgb, var(--color-primary) 8%, transparent) 0%, color-mix(in srgb, var(--color-primary) 20%, transparent) 48%, color-mix(in srgb, var(--session-accent-ring) 26%, transparent) 100%)",
                }}
              />
              <div
                className="absolute left-0 top-[calc(50%+2px)] h-[3px] -translate-y-1/2 rounded-full"
                style={{
                  ...progressHighlightStyle,
                  background:
                    "linear-gradient(90deg, color-mix(in srgb, var(--color-primary) 16%, transparent) 0%, color-mix(in srgb, var(--color-primary) 34%, transparent) 44%, color-mix(in srgb, var(--session-accent-ring) 78%, transparent) 82%, color-mix(in srgb, var(--session-accent-ink) 92%, black 8%) 100%)",
                  boxShadow:
                    "0 0 12px color-mix(in srgb, var(--color-primary) 28%, transparent), 0 0 22px color-mix(in srgb, var(--session-accent-ring) 18%, transparent)",
                }}
              />
            </div>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={0.5}
            value={activeProgressPercent}
            onInput={handleSeekInput}
            onPointerDown={handleSeekPointerDown}
            onPointerUp={handleSeekPointerUp}
            onPointerCancel={handleSeekPointerUp}
            onBlur={handleSeekBlur}
            disabled={controlsDisabled}
            style={progressStyle}
            className={cn(
              "absolute inset-x-0 top-[2px] m-0 h-6 w-full -translate-y-[62%] appearance-none bg-transparent touch-pan-x [-webkit-tap-highlight-color:transparent] [--transport-progress-elapsed:var(--session-accent-gradient-strong)] [--transport-progress-pct:0%] [--transport-progress-remaining:color-mix(in_srgb,var(--surface-card)_88%,black_12%)] [--transport-progress-thumb-size:18px] [--transport-progress-track-h:8px]",
              controlsDisabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
            )}
            data-transport-progress=""
            aria-label="Track position"
          />
        </div>

        <div className="relative grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-[0.75rem] px-[0.95rem] pb-[max(0.55rem,env(safe-area-inset-bottom))] pt-[1.2rem] md:px-[1.1rem] md:pt-[1.25rem] lg:gap-[0.9rem] 2xl:gap-3 2xl:px-[clamp(1.5rem,1.8vw,2.2rem)] 2xl:pt-[1.45rem]">
          <div
            className="relative min-w-0 px-[0.2rem] py-1"
            aria-live="polite"
            data-ui-test="transport-now-playing"
            data-track-uri={trackUri ?? ""}
            data-track-title={safeTitle}
            data-track-artist={safeArtist}
          >
            <div
              className="truncate whitespace-nowrap text-sm font-semibold leading-[1.2] text-foreground md:text-[0.9375rem] lg:text-base 2xl:text-[1rem]"
              data-ui-test="transport-track-title"
            >
              {safeTitle}
            </div>
            {safeArtist ? (
              <div
                className="truncate whitespace-nowrap text-[0.75rem] leading-[1.25] text-muted-foreground md:text-[0.8125rem] lg:text-sm 2xl:text-[0.9375rem]"
                data-ui-test="transport-track-artist"
              >
                {safeArtist}
              </div>
            ) : null}
          </div>
          <div className="relative flex items-center justify-center gap-[0.35rem] px-[0.15rem]">
            <TransportIconButton
              ariaLabel="Previous track"
              onClick={onPrev}
              disabled={controlsDisabled}
            >
              <SkipBack className="size-[1.15rem] lg:size-[1.2rem]" weight="bold" />
            </TransportIconButton>
            <TransportIconButton
              ariaLabel={isPlaying ? "Pause" : "Play"}
              onClick={onPlayPause}
              disabled={controlsDisabled}
              tone="primary"
              className="size-10 hover:brightness-[1.02] md:size-[2.65rem] 2xl:size-[2.8rem]"
            >
              {isPlaying ? (
                <Pause className="size-[1.2rem] md:size-[1.3rem] 2xl:size-[1.4rem]" weight="fill" />
              ) : (
                <Play className="size-[1.2rem] md:size-[1.3rem] 2xl:size-[1.4rem]" weight="fill" />
              )}
            </TransportIconButton>
            <TransportIconButton
              ariaLabel="Next track"
              onClick={onNext}
              disabled={controlsDisabled}
            >
              <SkipForward className="size-[1.15rem] lg:size-[1.2rem]" weight="bold" />
            </TransportIconButton>
          </div>
          <div className="relative flex min-w-0 items-center justify-end gap-2 p-0 text-[0.75rem] text-muted-foreground lg:text-sm 2xl:text-[0.9375rem]">
            {onEndSession ? (
              <TooltipProvider delayDuration={120}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <TransportIconButton
                        ariaLabel="End session"
                        onClick={onEndSession}
                        disabled={endSessionDisabled}
                        tone="tertiary"
                      >
                        <StopCircle
                          className="size-[1.05rem] lg:size-[1.15rem] 2xl:size-[1.2rem]"
                          weight="regular"
                        />
                      </TransportIconButton>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    End session
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            <div className="hidden md:flex">
              <div className="inline-flex items-center gap-2 border-0 bg-transparent p-0 2xl:px-3">
                <TransportIconButton
                  ariaLabel={safeVolume <= 0 ? "Restore volume" : "Mute volume"}
                  onClick={handleToggleMute}
                  disabled={!onVolumeChange}
                  tone={safeVolume <= 0 ? "primary" : "secondary"}
                >
                  {safeVolume <= 0 ? (
                    <SpeakerSlash className="size-[1.1rem] lg:size-[1.2rem] 2xl:size-[1.3rem]" weight="fill" />
                  ) : (
                    <SpeakerHigh className="size-[1.1rem] lg:size-[1.2rem] 2xl:size-[1.3rem]" weight="fill" />
                  )}
                </TransportIconButton>
                <span aria-hidden="true" className="h-5 w-px rounded-full bg-border/80 lg:h-6" />
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={volumePercent}
                  onInput={handleVolumeInput}
                  disabled={!onVolumeChange}
                  style={volumeStyle}
                  className="m-0 h-5 w-[5.35rem] appearance-none bg-transparent p-0 align-middle touch-pan-x [-webkit-tap-highlight-color:transparent] [--transport-volume-elapsed:color-mix(in_srgb,var(--session-accent-ink)_74%,var(--color-info)_26%)] [--transport-volume-pct:0%] [--transport-volume-remaining:color-mix(in_srgb,var(--surface-card)_74%,white_26%)] [--transport-volume-thumb-size:15px] [--transport-volume-track-h:8px] lg:w-[6.1rem] 2xl:w-[7rem]"
                  data-transport-volume=""
                  aria-label="Volume"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
